import EngineCleanupBase from '../../../services/engines/EngineCleanupBase';
import componentNotifier from '../../../services/wss/ComponentNotifier';
import { EngineNames } from '../../../services/wss/events/EngineNames';
import umlPreviewCache from './UmlPreviewCache';
import umlPreviewFetcher from './UmlPreviewFetcher';

const _name = EngineNames.UmlPreviewEngine;

/**
 * Orchestrates preview-note lookups for UmlSchema notes (title convention
 * `${schemaRk}.preview`). This is what lets UmlPreviewUpdater UPDATE an
 * existing preview instead of creating a new one every run — that lookup is
 * this engine's job, not the updater's (separation per Engine Pattern).
 *
 * Same shape as UmlNotesEngine: fetchAll() once (meant to be called from
 * PageContent.componentDidMount, same precedent as umlNotesEngine.fetchAll),
 * consumers read via getPreviewRk() afterwards — no lazy per-lookup fetching.
 */
class UmlPreviewEngine extends EngineCleanupBase {
    bootup = () => {
        this.initialize(_name);
    };

    /**
     * Fetch alle UmlSchemaPreview-notes van de BE (1x, zie class-doc). Consumers
     * moeten dit zelf NIET aanroepen — lees via getPreviewRk().
     */
    fetchAll = async () => {
        await umlPreviewFetcher.fetch();
        this._notify();
    };

    /**
     * Returns the cached preview note rk for a schema, or null if none exists.
     * @param {string} schemaRk
     * @returns {string|null}
     */
    getPreviewRk = (schemaRk) => {
        return umlPreviewCache.getBySchemeRk(schemaRk)?.rk ?? null;
    };

    /**
     * Returns the full cached preview note for a schema (body holds the PNG
     * data URL), or null if none exists yet. Used by LoadUmlNoteRow to render
     * a thumbnail.
     * @param {string} schemaRk
     * @returns {object|null}
     */
    getPreview = (schemaRk) => {
        return umlPreviewCache.getBySchemeRk(schemaRk);
    };

    /**
     * Adds/updates a preview note in cache — called by UmlPreviewUpdater right
     * after a successful save, so getPreviewRk reflects it immediately without
     * waiting for the next fetchAll.
     * @param {object} note
     */
    upsert = (note) => {
        try {
            if (!note?.rk) return;
            umlPreviewCache.addOrUpdate([note]);
            this._notify();
        } catch (error) {
            this.logError('upsert', error.message);
        }
    };

    clear = () => {
        umlPreviewCache.clear();
        super.clear();
    };

    // ─── private ──────────────────────────────────────────

    _notify = () => {
        componentNotifier.notify(_name, 'dataChanged', {});
    };
}

const umlPreviewEngine = new UmlPreviewEngine();
export default umlPreviewEngine;
